import '@/App.css';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AuthGuard from '@/components/AuthGuard';
import { BUILD_VERSION } from '@/lib/version';
import ClientLayout from '@/components/ClientLayout';

import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Onboarding from '@/pages/Onboarding';
import Checkin from '@/pages/Checkin';
import Notifications from '@/pages/Notifications';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';

import Home from '@/pages/client/Home';
import Training from '@/pages/client/Training';
import Nutrition from '@/pages/client/Nutrition';
import Progress from '@/pages/client/Progress';
import Profile from '@/pages/client/Profile';

import CoachClients from '@/pages/coach/Clients';
import CoachClientDetail from '@/pages/coach/ClientDetail';
import CoachLibrary from '@/pages/coach/Library';
import EditTraining from '@/pages/coach/EditTraining';
import EditNutrition from '@/pages/coach/EditNutrition';
import CoachSettings from '@/pages/coach/Settings';

// Stale-bundle guard: if the deployed backend reports a different build version
// than this loaded bundle, reload exactly once (tracked in sessionStorage) so a
// user never keeps running an old frontend against a new backend. Every failure
// path (backend down, 500, timeout, malformed) is a silent no-op — never blocks
// the app, never logs out, never touches data.
async function checkBundleVersion() {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    const res = await fetch(`${base}/api/version`, { cache: 'no-store', signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return;                                   // backend error -> do nothing
    const data = await res.json();
    const backendVersion = data && data.version;
    if (!backendVersion || backendVersion === BUILD_VERSION) return; // in sync -> no reload
    const key = `bundle_reloaded_${backendVersion}`;
    if (sessionStorage.getItem(key)) return;               // already reloaded once -> no loop
    sessionStorage.setItem(key, '1');
    window.location.reload();
  } catch (_) { /* network / timeout / parse error -> app runs normally */ }
}

function App() {
  useEffect(() => { checkBundleVersion(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route element={<AuthGuard role="client"><ClientLayout /></AuthGuard>}>
            <Route path="/app/home" element={<Home />} />
            <Route path="/app/training" element={<Training />} />
            <Route path="/app/nutrition" element={<Nutrition />} />
            <Route path="/app/progress" element={<Progress />} />
            <Route path="/app/profil" element={<Profile />} />
          </Route>

          <Route path="/checkin" element={<AuthGuard><Checkin /></AuthGuard>} />
          <Route path="/notifications" element={<AuthGuard><Notifications /></AuthGuard>} />

          <Route path="/coach/clients" element={<AuthGuard role="admin"><CoachClients /></AuthGuard>} />
          <Route path="/coach/client/:id" element={<AuthGuard role="admin"><CoachClientDetail /></AuthGuard>} />
          <Route path="/coach/library" element={<AuthGuard role="admin"><CoachLibrary /></AuthGuard>} />
          <Route path="/coach/settings" element={<AuthGuard role="admin"><CoachSettings /></AuthGuard>} />
          <Route path="/coach/edit-training/:id" element={<AuthGuard role="admin"><EditTraining /></AuthGuard>} />
          <Route path="/coach/edit-nutrition/:id" element={<AuthGuard role="admin"><EditNutrition /></AuthGuard>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
